import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { EyeIcon, EyeOffIcon, PencilIcon, PlusIcon, SendIcon, StarIcon, Trash2Icon, ClapperboardIcon } from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DemoBadge } from '../../components/DemoBadge';
import { EmptyState } from '../../components/EmptyState';
import { PosterFallback } from '../../components/PosterFallback';
import { useCatalog } from '../../contexts/CatalogContext';
import { integrations } from '../../data/integrations';
import { CATEGORY_LABELS, formatShortDate, STATUS_LABELS } from '../../utils/production';
import { buttonClass, pagePadding } from '../../utils/styles';
import type { Production, PublicationStatus } from '../../types/production';
type Filter = 'all' | PublicationStatus;
const FILTERS: {
  value: Filter;
  label: string;
}[] = [{
  value: 'all',
  label: 'Todas'
}, {
  value: 'published',
  label: 'Publicadas'
}, {
  value: 'draft',
  label: 'Borradores'
}, {
  value: 'scheduled',
  label: 'Programadas'
}];
const actionButton = 'inline-flex h-9 w-9 items-center justify-center rounded-md text-mist transition-colors duration-150 ease-out hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40';
export function AdminDashboard() {
  const {
    productions,
    setProductionStatus,
    deleteProduction
  } = useCatalog();
  const [filter, setFilter] = useState<Filter>('all');
  const [pendingDelete, setPendingDelete] = useState<Production | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const visible = filter === 'all' ? productions : productions.filter(p => p.status === filter);
  const count = (f: Filter) => f === 'all' ? productions.length : productions.filter(p => p.status === f).length;
  const toggleStatus = async (p: Production) => {
    const next: PublicationStatus = p.status === 'published' ? 'draft' : 'published';
    setBusyId(p.id);
    try {
      await setProductionStatus(p.id, next);
      toast.success(next === 'published' ? `«${p.title}» publicada` : `«${p.title}» retirada del catálogo`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo cambiar el estado.');
    } finally {
      setBusyId(null);
    }
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteProduction(pendingDelete.id);
      toast.success(`«${pendingDelete.title}» eliminada`);
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo eliminar.');
    } finally {
      setDeleting(false);
    }
  };
  const cancelDelete = useCallback(() => setPendingDelete(null), []);
  return <div className={`pb-24 pt-24 md:pt-28 ${pagePadding}`} data-id="element-321" data-mp="1ffa9o9qgzc6t">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-322" data-mp="1fdpfil82hv9h">
        <div data-id="element-323" data-mp="17vhshsm125hg">
          <p className="text-sm text-mist" data-id="element-324" data-mp="17u6mrn90unnp">Administración</p>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight md:text-4xl" data-id="element-325" data-mp="12yhmtit3b4qv">Publicaciones</h1>
        </div>
        <Link to="/admin/nueva" className={buttonClass('primary', 'md')} data-id="element-326" data-mp="xwa5edphqwh5">
          <PlusIcon className="h-4 w-4" data-id="element-327" data-mp="tvgjtqzittrf" />
          Nueva publicación
        </Link>
      </div>

      <div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]" data-id="element-328" data-mp="oygjzxt9ln8r">
        <section aria-label="Listado de publicaciones" data-id="element-329" data-mp="k66pe4h7fz05">
          <div role="tablist" aria-label="Filtrar por estado" className="scrollbar-none flex gap-1 overflow-x-auto border-b border-graphite" data-id="element-330" data-mp="k4ltobj5ma0p">
            {FILTERS.map(f => {
            const active = filter === f.value;
            return <button key={f.value} role="tab" type="button" aria-selected={active} onClick={() => setFilter(f.value)} className={`relative whitespace-nowrap px-3 py-3 text-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${active ? 'font-semibold text-white' : 'text-mist hover:text-white'}`} data-id="element-331" data-mp="6771iz1ino51">
                  {f.label} <span className="ml-1 text-mist" data-id="element-332" data-mp="1hm2z8qyg42jd">{count(f.value)}</span>
                  {active && <span aria-hidden="true" className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-action" data-id="element-333" data-mp="1ho7e23xvulyf" />}
                </button>;
          })}
          </div>

          {visible.length === 0 ? <EmptyState icon={ClapperboardIcon} title="No hay publicaciones aquí" description="Crea una nueva publicación o cambia el filtro." action={<Link to="/admin/nueva" className={buttonClass('secondary', 'md')} data-id="element-335" data-mp="37z25veyhxdqz">
                  <PlusIcon className="h-4 w-4" data-id="element-336" data-mp="3i27iqaqctr4q" />
                  Nueva publicación
                </Link>} data-id="element-334" data-mp="1dk7ybnkffr64" /> : <ul className="divide-y divide-graphite" data-id="element-337" data-mp="3d4xjy4h2s348">
              {visible.map(p => {
            const busy = busyId === p.id;
            return <li key={p.id} className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center" data-id="element-338" data-mp="3m3j2w0qglk5j">
                    <div className="flex min-w-0 flex-1 items-center gap-4" data-id="element-339" data-mp="4mrollca8xec">
                      <div className="relative aspect-video w-28 shrink-0 overflow-hidden rounded bg-coal md:w-36" data-id="element-340" data-mp="3vtud250qkpkv">
                        {p.posterUrl ? <img src={p.posterUrl} alt="" loading="lazy" className="h-full w-full object-cover" data-id="element-341" data-mp="dng3ei9i31sa" /> : <PosterFallback data-id="element-342" data-mp="2ffcczmc3y1kf" />}
                      </div>
                      <div className="min-w-0" data-id="element-343" data-mp="2beabszymrfft">
                        <div className="flex flex-wrap items-center gap-2" data-id="element-344" data-mp="2bflf6yo5yl6e">
                          <p className="truncate font-display font-bold text-white" data-id="element-345" data-mp="ee61pjjqzhxv">{p.title}</p>
                          {p.isFeatured && <span className="inline-flex items-center gap-1 text-xs text-white" data-id="element-346" data-mp="jek2qhmxlrme">
                              <StarIcon aria-hidden="true" className="h-3.5 w-3.5 fill-current" data-id="element-347" data-mp="odn8636tpzuw" />
                              Destacado
                            </span>}
                          {p.isDemo && <DemoBadge data-id="element-348" data-mp="xaebn0npggzi" />}
                        </div>
                        <p className="mt-1 text-sm text-mist" data-id="element-349" data-mp="129r0fqy4zy4q">
                          {[p.category ? CATEGORY_LABELS[p.category] : 'Sin categoría', p.videoUrl ? 'Con video' : 'Sin video'].join(' · ')}
                        </p>
                        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-id="element-350" data-mp="1c2zij6dw0dys">
                          <span className={`rounded-sm px-1.5 py-0.5 font-semibold ${p.status === 'published' ? 'bg-white text-night' : p.status === 'scheduled' ? 'border border-white/40 text-white' : 'border border-graphite text-mist'}`} data-id="element-351" data-mp="1gy523yjthgjp">
                            {STATUS_LABELS[p.status]}
                          </span>
                          {p.status === 'scheduled' && <span className="text-mist" data-id="element-352" data-mp="3p9nxd8300o8v">Estreno {formatShortDate(p.releaseDate)}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:shrink-0" data-id="element-353" data-mp="3fg74c6aord69">
                      <Link to={`/titulo/${p.slug}`} aria-label={`Ver ficha de ${p.title}`} title="Ver ficha" className={actionButton} data-id="element-354" data-mp="3ahx4hie0noeb">
                        <EyeIcon className="h-4 w-4" data-id="element-355" data-mp="bv1dvur0b45w" />
                      </Link>
                      <Link to={`/admin/editar/${p.id}`} aria-label={`Editar ${p.title}`} title="Editar" className={actionButton} data-id="element-356" data-mp="70x4z5y6km26">
                        <PencilIcon className="h-4 w-4" data-id="element-357" data-mp="3garjocem3mqd" />
                      </Link>
                      <button type="button" disabled={busy} onClick={() => void toggleStatus(p)} aria-label={p.status === 'published' ? `Despublicar ${p.title}` : `Publicar ${p.title} ahora`} title={p.status === 'published' ? 'Despublicar' : 'Publicar ahora'} className={actionButton} data-id="element-358" data-mp="3gcm4zrtspguz">
                        {p.status === 'published' ? <EyeOffIcon className="h-4 w-4" data-id="element-359" data-mp="3l9kyq99jrlsn" /> : <SendIcon className="h-4 w-4" data-id="element-360" data-mp="3q8p5kt9017ft" />}
                      </button>
                      <button type="button" onClick={() => setPendingDelete(p)} aria-label={`Eliminar ${p.title}`} title="Eliminar" className={actionButton} data-id="element-361" data-mp="3qas2lx8kk0u1">
                        <Trash2Icon className="h-4 w-4" data-id="element-362" data-mp="22b6jx49izaga" />
                      </button>
                    </div>
                  </li>;
          })}
            </ul>}
        </section>

        <aside aria-labelledby="integrations-title" className="h-fit rounded-xl border border-graphite bg-coal p-5" data-id="element-363" data-mp="2bc4fcy5we9n9">
          <h2 id="integrations-title" className="font-display text-base font-bold" data-id="element-364" data-mp="1nlij0459adt3">Estado de las integraciones</h2>
          <p className="mt-1 text-xs leading-relaxed text-mist" data-id="element-365" data-mp="1ioia7lh0uwya">
            Nada de lo siguiente está operativo hasta comprobar la conexión correspondiente.
          </p>
          <ul className="mt-5 space-y-4" data-id="element-366" data-mp="1skv16bl6mwpc">
            {integrations.map(item => <li key={item.name} data-id="element-367" data-mp="14vivhlp2d6rs">
                <div className="flex items-center justify-between gap-3" data-id="element-368" data-mp="14ur28hixvl4o">
                  <p className="text-sm font-medium text-white" data-id="element-369" data-mp="zx7odiq5lad9">{item.name}</p>
                  <span className="whitespace-nowrap rounded-sm border border-graphite px-1.5 py-0.5 text-[11px] text-mist" data-id="element-370" data-mp="2qblo749rvgye">
                    {item.status}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-mist" data-id="element-371" data-mp="2gd5y94mhedqh">{item.detail}</p>
              </li>)}
          </ul>
        </aside>
      </div>

      <ConfirmDialog open={!!pendingDelete} title="¿Eliminar esta publicación?" description={`«${pendingDelete?.title ?? ''}» se eliminará del catálogo y de administración. Esta acción no se puede deshacer.`} confirmLabel="Eliminar" busy={deleting} onConfirm={() => void confirmDelete()} onCancel={cancelDelete} data-id="element-372" data-mp="2be34igbybtxt" />
    </div>;
}